<template>
  <div>
    <v-skeleton-loader v-if="isLoading" type="card" />

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <template v-if="!isLoading && !error && feedMapping">
      <div class="text-subtitle-2 font-weight-bold mb-3">Derived Inventory Sources</div>
      <div v-if="derivedPrimarySources.length > 0" class="d-flex flex-wrap ga-2 mb-4">
        <v-chip v-for="src in derivedPrimarySources" :key="src" color="primary" variant="tonal" size="small">
          {{ formatFeedLabel(src) }}
        </v-chip>
      </div>
      <div v-else class="text-body-2 text-medium-emphasis mb-4">No primary sources derived.</div>

      <div class="text-subtitle-2 font-weight-bold mb-3">Feed Assignments by Source</div>
      <v-table v-if="feedAssignmentSummary.length > 0" density="compact" class="mb-4">
        <thead>
          <tr>
            <th class="text-left">Feed</th>
            <th class="text-left">Condition</th>
            <th class="text-left">States</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in feedAssignmentSummary" :key="i">
            <td class="font-weight-medium">{{ formatFeedLabel(row.feed) }}</td>
            <td>{{ row.condition }}</td>
            <td>
              <v-chip
                v-for="state in row.states"
                :key="state"
                size="x-small"
                variant="tonal"
                class="me-1 mb-1"
              >
                {{ stateLabel(state) }}
              </v-chip>
            </td>
          </tr>
        </tbody>
      </v-table>
      <div v-else class="text-body-2 text-medium-emphasis mb-4">No feed assignments configured.</div>

      <v-expansion-panels variant="accordion" class="mb-4">
        <v-expansion-panel>
          <v-expansion-panel-title class="text-subtitle-2">
            Generated Publish Rules
            <v-chip size="x-small" color="grey" variant="tonal" class="ms-2">
              {{ generatedRuleCount }}
            </v-chip>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div v-if="generatedRules.length === 0" class="text-body-2 text-medium-emphasis pa-2">
              No auto-generated publish rules.
            </div>
            <v-card
              v-for="(rule, i) in generatedRules"
              :key="i"
              variant="outlined"
              class="mb-2 pa-3"
            >
              <div class="text-caption font-weight-bold text-grey-darken-1 mb-1">
                {{ rule.reason || `Rule #${i + 1}` }}
              </div>
              <div class="text-caption text-mono">
                <span v-for="(sub, j) in rule.rules" :key="j">
                  <span v-if="j > 0" class="text-grey mx-1">{{ rule.logic }}</span>
                  <code>{{ sub.field }} {{ sub.operator }} {{ formatValue(sub.value) }}</code>
                </span>
              </div>
              <div class="text-caption text-grey mt-1">
                Action: <strong>{{ rule.action || 'block' }}</strong>
              </div>
            </v-card>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <div v-if="!isLoading && !error && !feedMapping" class="text-body-2 text-medium-emphasis">
      No feed mapping configured for this account.
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useListingConfigStore } from '@/stores/listingConfig'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import { fetchFeedMapping } from '@/services/feedMappingService'
import { VEHICLE_STATE_LABELS, type FeedMapping } from '@/types/feedMapping'

const appConfig = useJazelAppConfig()
const accountSettings = useAccountSettingsStore()
const listingStore = useListingConfigStore()

const isLoading = ref(false)
const error = ref('')
const feedMapping = ref<FeedMapping | null>(null)

type AssignmentRow = { feed: string; condition: string; states: string[] }

const FEED_LABEL_MAP: Record<string, string> = {
  vauto: 'VAuto',
  fdds1: 'FDDS1',
  fdds2: 'FDDS2',
  homenet: 'HomeNet',
  homenet_api: 'HomeNet API',
  locate: 'Locate',
  icc: 'ICC',
  idms: 'IDMS',
  dealerslink: 'DealersLink',
}

function formatFeedLabel(name: string): string {
  const base = String(name || '').trim()
  if (FEED_LABEL_MAP[base]) return FEED_LABEL_MAP[base]
  return base.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function stateLabel(state: string): string {
  return VEHICLE_STATE_LABELS[state] ?? state
}

function formatValue(val: unknown): string {
  if (Array.isArray(val)) return `[${val.map((v) => JSON.stringify(v)).join(', ')}]`
  return JSON.stringify(val)
}

const derivedPrimarySources = computed((): string[] => {
  if (!feedMapping.value) return []
  const feeds = new Set<string>()
  for (const stateMap of Object.values(feedMapping.value)) {
    for (const feed of Object.values(stateMap)) {
      if (feed) feeds.add(feed)
    }
  }
  return Array.from(feeds).sort()
})

const feedAssignmentSummary = computed((): AssignmentRow[] => {
  if (!feedMapping.value) return []
  const rows: AssignmentRow[] = []
  for (const [condition, stateMap] of Object.entries(feedMapping.value)) {
    const byFeed = new Map<string, string[]>()
    for (const [state, feed] of Object.entries(stateMap)) {
      if (!feed) continue
      if (!byFeed.has(feed)) byFeed.set(feed, [])
      byFeed.get(feed)!.push(state)
    }
    for (const [feed, states] of byFeed) {
      rows.push({
        feed,
        condition: condition.charAt(0).toUpperCase() + condition.slice(1),
        states,
      })
    }
  }
  return rows
})

const generatedRules = computed(() => {
  const allRules = listingStore.listingConfig?.all?.inventory_membership_rules?.ruleset?.rules || []
  return allRules.filter((r: any) => r?._feedMappingGenerated) as any[]
})

const generatedRuleCount = computed(() => generatedRules.value.length)

function resolveIseEndpoint(): string {
  const cfg = (appConfig as any) || {}
  return String(cfg?.iseBackendUrl || import.meta.env.VITE_ISE_BACKEND_URL || '').replace(/\/+$/g, '')
}

async function loadSummary() {
  isLoading.value = true
  error.value = ''
  try {
    const iseEndpoint = resolveIseEndpoint()
    const accountId = String(accountSettings.accountId || '').trim()
    const token = String(accountSettings.sessionToken || '').trim()
    if (!iseEndpoint || !accountId) {
      error.value = 'Missing ISE endpoint or account id.'
      return
    }

    const [mappingResp] = await Promise.all([
      fetchFeedMapping(iseEndpoint, accountId, { token }),
      listingStore.listingConfig ? Promise.resolve() : listingStore.getListingConfig(),
    ])

    feedMapping.value = mappingResp?.feedMapping ?? null
  } catch (e: any) {
    error.value = e?.message || 'Failed to load feed routing summary.'
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  void loadSummary()
})
</script>

<style scoped>
.text-mono code {
  font-family: monospace;
  font-size: 0.85em;
  background: rgba(0, 0, 0, 0.04);
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
